import { Fragment, useMemo, useState } from "react";
import PropTypes from "prop-types";

import ActionButton from "../../formInputs/buttons/ActionButton";

import "../../../styles/DifferentiationTable.scss";

const DifferentiatedStudentsTable = ({ students, onEdit, onReset, onSetupOthers }) => {
    const [filterText, setFilterText] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "lastName", direction: "ascending" });
    const filteredData = useMemo(() => {
        if (!filterText) return students;
        return students.filter((item) =>
            Object.keys(item).some((key) => item[key]?.toString().toLowerCase().includes(filterText.toLowerCase()))
        );
    }, [students, filterText]);

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;
        return [...filteredData].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            // Use numeric comparison for numeric columns
            const numericColumns = ["studentNumber", "excludedCount", "addedCount"];
            if (numericColumns.includes(sortConfig.key)) {
                const aNum = Number(aValue) || 0;
                const bNum = Number(bValue) || 0;
                return sortConfig.direction === "ascending" ? aNum - bNum : bNum - aNum;
            }

            // Use string comparison for text columns
            const aStr = aValue?.toString().toLowerCase() || "";
            const bStr = bValue?.toString().toLowerCase() || "";
            return sortConfig.direction === "ascending" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        });
    }, [filteredData, sortConfig]);

    const groupedSortedData = useMemo(() => {
        const groups = {};
        sortedData.forEach((student) => {
            const groupKey =
                student.source === "homeroom"
                    ? `Homeroom - ${student.homeroomGrade || "Unknown"}`
                    : `Shared: ${student.sharedGroupOwner || "Unknown"} - ${student.sharedGroupName || student.sourceProfile?.gradeName || "Unknown"}`;
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(student);
        });

        const sortedGroups = {};
        const homeroomKeys = Object.keys(groups)
            .filter((k) => k.startsWith("Homeroom"))
            .sort((a, b) => a.localeCompare(b));
        const sharedKeys = Object.keys(groups)
            .filter((k) => k.startsWith("Shared:"))
            .sort((a, b) => a.localeCompare(b));

        [...homeroomKeys, ...sharedKeys].forEach((key) => {
            sortedGroups[key] = groups[key];
        });

        return sortedGroups;
    }, [sortedData]);

    const requestSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "ascending" ? "descending" : "ascending"
        }));
    };

    const renderSortIcons = (columnKey) => {
        if (sortConfig.key === columnKey) {
            if (sortConfig.direction === "ascending") {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up-fill" style={{ height: "16px" }} />
                        <i className="bi bi-caret-down" style={{ height: "16px", marginBottom: "5px" }} />
                    </span>
                );
            } else {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up" style={{ height: "16px" }} />
                        <i className="bi bi-caret-down-fill" style={{ height: "16px", marginBottom: "5px" }} />
                    </span>
                );
            }
        }
        return (
            <span className="icon-container">
                <i className="bi bi-caret-up" style={{ height: "16px" }} />
                <i className="bi bi-caret-down" style={{ height: "16px", marginBottom: "5px" }} />
            </span>
        );
    };

    return (
        <div className="differentiation-table">
            <div className="filter-wrapper">
                <h5>Students with Differentiated Content ({students?.length})</h5>
                <label className="roster-table-filter-label">
                    Filter Students:{" "}
                    <input
                        className="roster-table-filter"
                        type="text"
                        placeholder="Filter students"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </label>
            </div>
            <div className="table-wrapper">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th>
                                <button
                                    type="button"
                                    onClick={() => requestSort("lastName")}
                                    className="table-header-button"
                                    aria-label="Sort by last name"
                                >
                                    Last Name {renderSortIcons("lastName")}
                                </button>
                            </th>
                            <th>
                                <button
                                    type="button"
                                    onClick={() => requestSort("firstName")}
                                    className="table-header-button"
                                    aria-label="Sort by first name"
                                >
                                    First Name {renderSortIcons("firstName")}
                                </button>
                            </th>
                            <th>
                                <button
                                    type="button"
                                    onClick={() => requestSort("studentNumber")}
                                    className="table-header-button"
                                    aria-label="Sort by student number"
                                >
                                    Student Number {renderSortIcons("studentNumber")}
                                </button>
                            </th>
                            <th style={{ width: "100px" }}>
                                <button
                                    type="button"
                                    onClick={() => requestSort("excludedCount")}
                                    className="table-header-button"
                                    aria-label="Sort by excluded items"
                                >
                                    Excluded Content {renderSortIcons("excludedCount")}
                                </button>
                            </th>
                            <th style={{ width: "140px" }}>
                                <button
                                    type="button"
                                    onClick={() => requestSort("addedCount")}
                                    className="table-header-button"
                                    aria-label="Sort by added items"
                                >
                                    Added Content {renderSortIcons("addedCount")}
                                </button>
                            </th>
                            <th style={{ width: "90px" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(groupedSortedData).length === 0 && (
                            <tr>
                                <td colSpan={6} style={{ textAlign: "center", padding: "24px" }}>
                                    No students found.
                                </td>
                            </tr>
                        )}
                        {Object.entries(groupedSortedData).map(([groupKey, groupStudents]) => (
                            <Fragment key={`group-${groupKey}`}>
                                <tr key={`header-${groupKey}`} className="teacher-group-header">
                                    <td
                                        colSpan={6}
                                        style={{ fontWeight: 700, backgroundColor: "#f8f9fa", padding: "8px" }}
                                    >
                                        {groupKey}
                                    </td>
                                </tr>
                                {groupStudents.map((student) => {
                                    const {
                                        firstName,
                                        lastName,
                                        studentNumber,
                                        gradingProfileStudentId,
                                        addedCount,
                                        excludedCount
                                    } = student;
                                    return (
                                        <tr key={gradingProfileStudentId}>
                                            <td>{lastName}</td>
                                            <td>{firstName}</td>
                                            <td>{studentNumber}</td>
                                            <td style={{ textAlign: "center" }}>{excludedCount || 0}</td>
                                            <td style={{ textAlign: "center" }}>{addedCount || 0}</td>
                                            <td style={{ textAlign: "center" }}>
                                                <button
                                                    aria-label={`Edit differentiation for ${firstName} ${lastName}`}
                                                    className="transparent-button"
                                                    onClick={() => onEdit(student)}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil" />
                                                </button>
                                                <button
                                                    aria-label={`Reset differentiation for ${firstName} ${lastName}`}
                                                    className="transparent-button"
                                                    onClick={() => onReset(student)}
                                                    title="Reset"
                                                >
                                                    <i className="bi bi-arrow-counterclockwise" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="action-buttons">
                <ActionButton label="Set Up New Differentiation" onClick={onSetupOthers} />
            </div>
        </div>
    );
};

DifferentiatedStudentsTable.propTypes = {
    students: PropTypes.array.isRequired,
    onEdit: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    onSetupOthers: PropTypes.func.isRequired
};

export default DifferentiatedStudentsTable;
